import { Component, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

@Component({
  standalone: false,
  selector: "plan-detail-validation-cell",
  templateUrl: "./plan-detail-validation-cell.component.html",
  styleUrls: ["./plan-detail-validation-cell.component.css"]
})
export class PlanDetailValidationCellComponent implements OnInit {
  public params: any;
  public validationSum: any;

  constructor(private translate: TranslateService) { }

  ngOnInit(): void {
  }

  agInit(params: any): void {
    this.params = params;
    // Expose the edit-field/read-only state on the actual gridcell element.
    this.applyGridCellAria();
    // Format according to the locale.
    /*this.validationSum = NumberUtils.formatNumberIfNecessary(params.context.columnSum(this.params.colDef.field),
      params.locale, params.metadata.decimal);*/
  }

  /**
   * AG Grid renders every value cell as role="gridcell" (kept intact so JAWS
   * table navigation - Ctrl+Alt+Arrow - keeps working AND so the WAI-ARIA grid
   * pattern stays valid: role="row" only allows gridcell/rowheader/columnheader
   * children). During grid navigation JAWS reads the FOCUSED .ag-cell element,
   * not its non-focusable descendants, so accessibility (name + value + state)
   * is applied to the gridcell itself (params.eGridCell).
   *
   * Editable value cells expose an accessible name in the form
   * "<param>, <column>, editable, value <value>. Press Enter to edit." so a
   * screen-reader user knows the cell can be edited and how to start editing it
   * (the row's pencil button is the primary entry point - see the row-header
   * cell - but individual value cells are still editable directly). Read-only
   * value cells (View Only Login, calculated/capped/closed cells and the pinned
   * Total/Average columns) expose "<param>, <column>, value <value>, read only."
   * so the read-only state is baked into the accessible NAME, which JAWS ALWAYS
   * announces for the focused gridcell (it does not reliably speak
   * aria-readonly on a role="gridcell"). We keep aria-readonly too for AT that
   * do honour it. This re-runs on every (re)render because refresh() returns
   * false, which forces AG Grid to recreate the component (agInit runs again) -
   * so the state stays correct across virtualization, scrolling and column
   * changes.
   */
  private applyGridCellAria(): void {
    const cell: HTMLElement | null | undefined = this.params?.eGridCell;
    if (!cell) {
      return;
    }
    if (this.isEditField || this.isComputedValueField) {
      const readOnly = this.isReadOnlyField;
      cell.setAttribute("aria-readonly", readOnly ? "true" : "false");
      cell.setAttribute("aria-label", this.buildValueCellAriaLabel(readOnly));
      cell.removeAttribute("aria-description");
    } else {
      cell.removeAttribute("aria-readonly");
      cell.removeAttribute("aria-label");
      // Editable parameter (row-header) cells carry the "Press Enter to edit"
      // hint as a description (NOT the accessible name), so JAWS announces it
      // when the cell is focused during grid navigation - matching the pencil's
      // own accessible name - WITHOUT changing the row-header name that data
      // cells reference (WCAG 4.1.2 / 4.1.3).
      if (this.isParamLabelCell && this.isRowEditable) {
        cell.setAttribute("aria-description", this.translate.instant("plan.detail.edit.row.header.hint"));
      } else {
        cell.removeAttribute("aria-description");
      }
    }
  }

  /**
   * Builds the gridcell accessible name for a value cell (an editable field OR a
   * calculated Total/Average cell): the descriptive row + column name, the
   * current value and the editable/read-only state - so JAWS announces all
   * three reliably. Editable cells also get the "Press Enter to edit" hint.
   */
  private buildValueCellAriaLabel(readOnly: boolean): string {
    const value = this.getValueToDisplay(this.params);
    const valueText = value === null || value === undefined ? "" : String(value);
    const key = readOnly
      ? "plan.detail.edit.cell.readonly.aria"
      : "plan.detail.edit.cell.editable.aria";
    return this.translate.instant(key, { field: this.fieldAriaLabel, value: valueText });
  }

  /**
   * True for the row's parameter-name (row-header) cell. This cell hosts the
   * inline "edit row" pencil button for editable rows.
   */
  get isParamLabelCell(): boolean {
    const field = this.params?.colDef?.field;
    // "plan_label_param" is the plan-detail row label column; "parameter" is the
    // equivalent row label column in the New Hire learning-curve grid. Both host
    // the row label + inline "edit row" pencil for editable rows.
    return field === "plan_label_param" || field === "parameter";
  }

  /**
   * True when this parameter (row-header) cell belongs to an editable row, so a
   * pencil button that starts editing the row should be shown. Delegates to the
   * grid context which knows the row permission and current grid-valid state.
   */
  get isRowEditable(): boolean {
    const context = this.params?.context;
    if (context && typeof context.isRowEditable === "function") {
      return context.isRowEditable(this.params?.node) === true;
    }
    return false;
  }

  /**
   * Accessible name for the inline pencil button (WCAG 2.5.3 / 4.1.2). The
   * native <button> role is announced automatically, so the label only needs to
   * name the row it edits and how to activate it, e.g. "Edit AHT (Productive)
   * row. Press Enter to edit this row.".
   */
  get rowEditAriaLabel(): string {
    return this.translate.instant("plan.detail.edit.row.button.aria", { row: this.rowEditLabel });
  }

  /**
   * Visible row label used in the pencil's accessible name / tooltip. Plan-detail
   * rows expose it as `plan_label_param`; the learning-curve grid has no such
   * field, so fall back to the cell's displayed value (the parameter label).
   */
  private get rowEditLabel(): string {
    return this.params?.data?.plan_label_param ?? this.getValueToDisplay(this.params) ?? "";
  }

  /**
   * Visible hover tooltip for the inline pencil button. Kept short (the full
   * instructions are in the accessible name / aria-description for AT).
   */
  get rowEditTooltip(): string {
    return this.translate.instant("plan.detail.edit.row.button.tooltip", { row: this.rowEditLabel });
  }

  /**
   * Starts editing the row from the pencil button. Delegates to the grid
   * context, which moves editing to the first editable cell of the row and
   * announces the edit-mode instructions (WCAG 4.1.3 Status Messages).
   */
  startRowEdit(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const context = this.params?.context;
    if (context && typeof context.startRowEditing === "function") {
      context.startRowEditing(this.params?.node);
    }
  }

  get isValidIndicator(){
    let context = this.params?.context;
    // Working-days invalid cells surface the indicator regardless of focus so
    // the user can see which week still holds values that must be cleared.
    if (context?.isWorkingDaysInvalidCell?.(this.params)) {
      return true;
    }
    const cellInvalid =
      context.isGridValid()==false &&
      context.isValidCell(this.params,this.params.colDef.field)==false;
    // Some grids (e.g. Add Learning Curve) show the indicator for every invalid
    // value regardless of focus (matching the reference design); others only
    // surface it on the currently focused cell to keep the grid uncluttered.
    if (cellInvalid && context?.alwaysShowInvalidIndicator?.()) {
      return true;
    }
    //return context.isGridValid()==false;
    return cellInvalid &&
      this.params.api.getFocusedCell() &&
      this.params.api.getFocusedCell().rowIndex==this.params.rowIndex &&
      this.params.api.getFocusedCell().column.colId==this.params.column.colId
  }
  get validationMessage(){
    let context = this.params?.context;
    const workingDaysMsg = context?.workingDaysValidationMessage?.(this.params);
    if (workingDaysMsg) {
      return workingDaysMsg;
    }
    return this.params.data.metadata.validator.msg;
  }

  /**
   * A value cell is an "edit field" only when its column is configured with an
   * `editable` function (the day/week/month value columns). Label columns
   * (plan_label_param/total/average) have no `editable` config, so they stay
   * plain cells. Exposing role="textbox" lets JAWS announce these cells as edit
   * fields (WCAG 4.1.2 Name, Role, Value).
   */
  get isEditField(): boolean {
    return !!this.params?.colDef?.editable;
  }

  /**
   * The pinned Total and Average columns show calculated summary values. They
   * are never editable, but they ARE data value cells (unlike the row's
   * parameter-name label), so a screen reader user needs to be told they are
   * read-only and cannot be edited.
   */
  get isComputedValueField(): boolean {
    const field = this.params?.colDef?.field;
    return field === "plan_label_total" || field === "plan_label_average";
  }

  /**
   * Reflects whether the cell can actually be edited right now. Mirrors AG
   * Grid's own editability (permission + valid-grid state) via the grid
   * context so read-only cells (e.g. View Only Login, calculated fields)
   * expose aria-readonly="true" and JAWS announces the read-only state.
   * Calculated Total/Average cells are always read-only.
   */
  get isReadOnlyField(): boolean {
    if (this.isComputedValueField) {
      return true;
    }
    if (!this.isEditField) {
      return false;
    }
    const context = this.params?.context;
    if (context && typeof context.cellEditable === "function") {
      return context.cellEditable(this.params) === false;
    }
    return false;
  }

  /**
   * Accessible name for the edit field. Combines the row's parameter label with
   * the column header (date) so every value cell gets a UNIQUE, descriptive
   * name that matches the visible row + column headers (e.g. "Contacts Received
   * (Forecast), Mar 2021"). A unique name lets Dragon NaturallySpeaking voice
   * users recognise and interact with each edit field by voice, and matches the
   * meaningful order screen readers announce (WCAG 4.1.2 Name, Role, Value).
   */
  get fieldAriaLabel(): string {
    const rowLabel = this.params?.data?.plan_label_param ?? "";
    return [rowLabel, this.columnLabel].filter(Boolean).join(", ");
  }

  /**
   * The column name for the accessible label. Value columns expose it via
   * colDef.headerName (the date), but the pinned Total/Average columns render
   * their header through a column-group and leave the child headerName empty,
   * so we fall back to their localised labels to keep the name meaningful.
   */
  private get columnLabel(): string {
    const headerName = this.params?.colDef?.headerName;
    if (headerName) {
      return headerName;
    }
    const field = this.params?.colDef?.field;
    if (field === "plan_label_total") {
      return this.translate.instant("plan.label.total");
    }
    if (field === "plan_label_average") {
      return this.translate.instant("plan.label.average");
    }
    return "";
  }

  getValueToDisplay(params) {
    return params.valueFormatted ? params.valueFormatted : params.value;
  }

  /**
   * True when the displayed value of an invalid cell is short (<= 4 digits, i.e.
   * it fits next to the error icon without truncating). Short values stay
   * RIGHT-aligned like every other numeric cell; longer values that would need
   * truncation are left as-is so their leading digits + ellipsis remain visible.
   * Only the digit characters are counted, so grouping separators / decimal
   * points do not push a small number over the threshold.
   */
  get isShortValue(): boolean {
    const value = this.getValueToDisplay(this.params);
    if (value === null || value === undefined) {
      return true;
    }
    const digitCount = (String(value).match(/\d/g) ?? []).length;
    return digitCount <= 4;
  }
  refresh(){
    return false;
  }

}
